// Play / Word panel, bottom center while a word is in progress: Clear latest,
// Clear all, the word, Submit. A tap on the panel anywhere but its buttons
// swaps it between the bottom and the top of the screen, so it never sits on
// the stars a player wants (Aaron, 2026-10-30); it glides there, eased, and
// settles with a small bounce. At the top it sits in the top bar's row,
// between the corner panels, or just below the bar where the row has no room
// for it. The next word appears where the last one left it; each level starts
// at the bottom, since the panel mounts afresh with each board.
import { useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode, type RefObject } from 'react';
import { useReducedMotion } from '../sky/reducedMotion';

/** The space kept between the panel and a corner panel beside it in the top row, px. */
const ROW_GAP = 10;
const TRAVEL_MS = 620;
/** Eases out of its spot and into the glide. */
const GLIDE = 'cubic-bezier(0.55, 0, 0.25, 1)';
/** The settle: a little past its stop, a little back, then still. */
const SETTLE = 'cubic-bezier(0.37, 0, 0.63, 1)';
/** How far past its stop it goes, px, at most. */
const OVERSHOOT = 9;
const FADE_MS = 120;

interface Props {
  /** No word in progress: the panel steps out of the way. */
  empty: boolean;
  /** The top HUD's corners, to see whether the top row has room. */
  topLeft: RefObject<HTMLElement | null>;
  topRight: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function WordPanel({ empty, topLeft, topRight, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [atTop, setAtTop] = useState(false);
  const [drop, setDrop] = useState(false);
  const reducedMotion = useReducedMotion();
  // Where the panel was before a swap, for the travel.
  const from = useRef<number | null>(null);

  // At the top: in the row if the word fits between the corners, else below the bar.
  const fitsRow = (): boolean => {
    const panel = panelRef.current;
    if (!panel) return true;
    const rowItems = topLeft.current?.querySelectorAll<HTMLElement>('.state-bar, .finish-btn, .selection-points') ?? [];
    const left = Math.max(0, ...Array.from(rowItems, (el) => el.getBoundingClientRect().right));
    const right = topRight.current?.getBoundingClientRect().left ?? window.innerWidth;
    const mid = window.innerWidth / 2;
    const half = panel.offsetWidth / 2;
    return mid - half >= left + ROW_GAP && mid + half <= right - ROW_GAP;
  };
  const fitsRowRef = useRef(fitsRow);
  fitsRowRef.current = fitsRow;

  // While at the top, check again as the word grows or the window changes.
  useLayoutEffect(() => {
    if (!atTop) return;
    const measure = () => setDrop(!fitsRowRef.current());
    const ro = new ResizeObserver(measure);
    if (panelRef.current) ro.observe(panelRef.current);
    if (topLeft.current) ro.observe(topLeft.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [atTop, topLeft]);

  // After a swap: travel from where it was, or fade in under reduced motion.
  useLayoutEffect(() => {
    const el = ref.current;
    const top = from.current;
    if (!el || top === null) return;
    from.current = null;
    if (reducedMotion) {
      el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: FADE_MS, easing: 'ease' });
      return;
    }
    const dy = top - el.getBoundingClientRect().top;
    // Past the stop in the direction of travel, then a smaller step back.
    const past = -Math.sign(dy) * Math.min(OVERSHOOT, Math.abs(dy) * 0.03);
    const at = (y: number) => `translate(-50%, ${y}px)`;
    el.animate(
      [
        { transform: at(dy), easing: GLIDE },
        { transform: at(past), offset: 0.68, easing: SETTLE },
        { transform: at(-past * 0.3), offset: 0.86, easing: SETTLE },
        { transform: at(0) },
      ],
      { duration: TRAVEL_MS },
    );
  }, [atTop, reducedMotion]);

  const swap = (e: MouseEvent) => {
    if ((e.target as Element).closest('button')) return;
    from.current = ref.current?.getBoundingClientRect().top ?? null;
    // Where it lands at the top is settled before it moves, so it travels there in one go.
    if (!atTop) setDrop(!fitsRow());
    setAtTop(!atTop);
  };

  const place = atTop ? (drop ? ' at-top drop' : ' at-top') : '';
  return (
    <div ref={ref} className={`hud hud-bottom${place}${empty ? ' is-empty' : ''}`} aria-hidden={empty}>
      <div ref={panelRef} className="word-panel panel" onClick={swap}>
        {children}
      </div>
    </div>
  );
}
